import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "react-query";
import api from "../services/api";
import { recruitingAPI } from "../services/recruitingApi";
import "./TodoList.css";

interface Todo {
  id: number;
  title: string;
  description?: string;
  priority: "low" | "medium" | "high";
  status: "pending" | "completed";
  due_date?: string;
  candidate_id?: number;
  candidate_name?: string;
  related_type?: string;
  related_id?: number;
}

interface Candidate {
  id: number;
  first_name: string;
  last_name: string;
  email?: string;
}

interface Customer {
  id: number;
  name: string;
  company_name?: string;
}

interface Job {
  id: number;
  title: string;
  department?: string;
  location?: string;
}

interface Application {
  id: number;
  candidate_first_name?: string;
  candidate_last_name?: string;
  job_title?: string;
}

interface TodoFilters {
  status?: string;
  priority?: string;
}

interface TodoListProps {
  userId?: string | number;
  filters?: TodoFilters;
  showFilters?: boolean;
  compact?: boolean;
}

interface FormData {
  title: string;
  description: string;
  priority: "low" | "medium" | "high";
  dueDate: string;
  relatedEntityType: string;
  relatedEntityId: string;
}

interface TodoData {
  title: string;
  description: string;
  priority: "low" | "medium" | "high";
  dueDate: string | null;
  relatedType: string | null;
  relatedId: number | null;
  candidateId: number | null;
}

const TodoList: React.FC<TodoListProps> = ({
  userId,
  filters = {},
  showFilters = true,
  compact = false,
}) => {
  const { t } = useTranslation("common");
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>(
    filters.status || "all",
  );
  const [priorityFilter, setPriorityFilter] = useState<string>(
    filters.priority || "all",
  );
  const [showForm, setShowForm] = useState<boolean>(false);
  const [editingTodo, setEditingTodo] = useState<Todo | null>(null);
  const [formData, setFormData] = useState<FormData>({
    title: "",
    description: "",
    priority: "medium",
    dueDate: "",
    relatedEntityType: "",
    relatedEntityId: "",
  });

  // Build query filters
  const queryFilters = {
    ...filters,
    status: statusFilter === "all" ? undefined : statusFilter,
    priority: priorityFilter === "all" ? undefined : priorityFilter,
  };

  // Fetch todos
  const { data, isLoading, error } = useQuery<{ data: Todo[] }, Error>(
    ["todos", queryFilters],
    () => api.todo.getTodos(queryFilters) as Promise<{ data: Todo[] }>,
    {
      refetchOnWindowFocus: false,
      staleTime: 30000, // 30 seconds
      retry: 1,
      onError: (err) => {
        console.error("Error fetching todos:", err);
      },
    },
  );

  // Fetch candidates for dropdown
  const { data: candidatesData } = useQuery<Candidate[]>(
    ["candidates-for-todo"],
    () => recruitingAPI.getCandidates({ limit: 100 }),
    {
      enabled: showForm,
      staleTime: 5 * 60 * 1000,
    },
  );

  // Fetch customers for dropdown
  const { data: customersData } = useQuery<Customer[]>(
    ["customers-for-todo"],
    () => recruitingAPI.getCustomers({ status: "active" }),
    {
      enabled: showForm && formData.relatedEntityType === "customer",
      staleTime: 5 * 60 * 1000,
    },
  );

  // Fetch jobs for dropdown
  const { data: jobsData } = useQuery<Job[]>(
    ["jobs-for-todo"],
    () => recruitingAPI.getJobs({ limit: 100 }),
    {
      enabled: showForm && formData.relatedEntityType === "job",
      staleTime: 5 * 60 * 1000,
    },
  );

  // Fetch applications for dropdown
  const { data: applicationsData } = useQuery<Application[]>(
    ["applications-for-todo"],
    () => recruitingAPI.getApplications(),
    {
      enabled: showForm && formData.relatedEntityType === "application",
      staleTime: 5 * 60 * 1000,
    },
  );

  // Mutations
  const createMutation = useMutation<unknown, Error, TodoData>(
    (todoData) => api.todo.createTodo(todoData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["todos"]);
        setShowForm(false);
        resetForm();
      },
    },
  );

  const updateMutation = useMutation<
    unknown,
    Error,
    { id: number; data: TodoData }
  >(({ id, data }) => api.todo.updateTodo(id, data), {
    onSuccess: () => {
      queryClient.invalidateQueries(["todos"]);
      setShowForm(false);
      setEditingTodo(null);
      resetForm();
    },
  });

  const deleteMutation = useMutation<unknown, Error, number>(
    (id) => api.todo.deleteTodo(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["todos"]);
      },
    },
  );

  const toggleStatusMutation = useMutation<
    unknown,
    Error,
    { id: number; status: string }
  >(({ id, status }) => api.todo.updateTodo(id, { status }), {
    onSuccess: () => {
      queryClient.invalidateQueries(["todos"]);
    },
  });

  const todos = data?.data || [];
  const pendingTodos = todos.filter((t) => t.status === "pending");
  const completedTodos = todos.filter((t) => t.status === "completed");

  // Limit displayed todos if showFilters is false (for dashboard)
  const displayPendingTodos = showFilters
    ? pendingTodos
    : pendingTodos.slice(0, 5);
  const displayCompletedTodos = showFilters ? completedTodos : [];

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      priority: "medium",
      dueDate: "",
      relatedEntityType: "",
      relatedEntityId: "",
    });
    setEditingTodo(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const todoData: TodoData = {
      title: formData.title,
      description: formData.description,
      priority: formData.priority,
      dueDate: formData.dueDate || null,
      relatedType: formData.relatedEntityType || null,
      relatedId: formData.relatedEntityId
        ? Number.parseInt(formData.relatedEntityId, 10)
        : null,
      candidateId:
        formData.relatedEntityType === "candidate" && formData.relatedEntityId
          ? Number.parseInt(formData.relatedEntityId, 10)
          : null,
    };

    if (editingTodo) {
      updateMutation.mutate({ id: editingTodo.id, data: todoData });
    } else {
      createMutation.mutate(todoData);
    }
  };

  const handleEdit = (todo: Todo) => {
    setEditingTodo(todo);
    // Determine the related entity type - prefer candidate_id if exists, otherwise use related_type
    const relatedEntityType = todo.candidate_id
      ? "candidate"
      : todo.related_type || "";
    const relatedEntityId = todo.candidate_id || todo.related_id || "";

    setFormData({
      title: todo.title,
      description: todo.description || "",
      priority: todo.priority,
      dueDate: todo.due_date ? todo.due_date.split("T")[0] : "",
      relatedEntityType: relatedEntityType,
      relatedEntityId: relatedEntityId ? String(relatedEntityId) : "",
    });
    setShowForm(true);
  };

  const handleDelete = (id: number) => {
    if (window.confirm(t("todo.deleteConfirm"))) {
      deleteMutation.mutate(id);
    }
  };

  const handleToggleStatus = (todo: Todo) => {
    const newStatus = todo.status === "pending" ? "completed" : "pending";
    toggleStatusMutation.mutate({ id: todo.id, status: newStatus });
  };

  const isOverdue = (dueDate?: string): boolean => {
    if (!dueDate) return false;
    return (
      new Date(dueDate) < new Date() &&
      new Date(dueDate).toDateString() !== new Date().toDateString()
    );
  };

  const getPriorityColor = (priority: string): string => {
    switch (priority) {
      case "high":
        return "#f44336";
      case "medium":
        return "#ff9800";
      case "low":
        return "#4caf50";
      default:
        return "#757575";
    }
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const renderTodoItem = (todo: Todo) => {
    const overdue = isOverdue(todo.due_date);

    return (
      <div
        className={`todo-item ${todo.status === "completed" ? "completed" : ""} ${overdue ? "overdue" : ""}`}
      >
        <div className="todo-item-header">
          <label className="todo-checkbox">
            <input
              type="checkbox"
              checked={todo.status === "completed"}
              onChange={() => handleToggleStatus(todo)}
              aria-label={`Mark ${todo.title} as ${todo.status === "completed" ? "pending" : "completed"}`}
            />
            <span className="todo-title">{todo.title}</span>
          </label>
          <div className="todo-actions">
            <span
              className="priority-badge"
              style={{ backgroundColor: getPriorityColor(todo.priority) }}
            >
              {todo.priority}
            </span>
            {!compact && (
              <>
                <span aria-hidden="true"> | </span>
                <button className="btn-edit" onClick={() => handleEdit(todo)}>
                  {t("actions.edit")}
                </button>
                <button
                  className="btn-delete"
                  onClick={() => handleDelete(todo.id)}
                >
                  {t("actions.delete")}
                </button>
              </>
            )}
          </div>
        </div>
        {todo.description && (
          <div className="todo-description">{todo.description}</div>
        )}
        <div className="todo-meta">
          {todo.due_date && (
            <span className={`todo-due-date ${overdue ? "overdue" : ""}`}>
              {t("todo.due")} {formatDate(todo.due_date)}
            </span>
          )}
          {todo.candidate_name && (
            <span className="todo-related">
              {t("todo.candidate")}: {todo.candidate_name}
            </span>
          )}
          {todo.related_type && todo.related_id && (
            <span className="todo-related">
              {todo.related_type}: {todo.related_id}
            </span>
          )}
        </div>
      </div>
    );
  };

  if (isLoading) {
    return <div className="todo-list-loading">{t("todo.loadingTodos")}</div>;
  }

  if (error) {
    return (
      <div className="todo-list-container">
        <div className="todo-list-header">
          <h2>{t("todo.myTasks")}</h2>
        </div>
        <div className="todo-list-error">
          {t("todo.errorLoadingTodos")}{" "}
          {(
            error as {
              response?: { data?: { error?: string; message?: string } };
              message?: string;
            }
          ).response?.data?.error ||
            (
              error as {
                response?: { data?: { error?: string; message?: string } };
                message?: string;
              }
            ).response?.data?.message ||
            error.message ||
            t("errors.somethingWentWrong")}
          <br />
          <small>{t("todo.backendHint")}</small>
        </div>
      </div>
    );
  }

  return (
    <div className="todo-list-container">
      {!compact && (
        <div className="todo-list-header">
          <h2>{t("todo.myTasks")}</h2>
          <button
            className="btn-primary"
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
          >
            {t("todo.newTask")}
          </button>
        </div>
      )}

      {showFilters && (
        <div className="todo-filters">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t("todo.allStatus")}</option>
            <option value="pending">{t("todo.pending")}</option>
            <option value="completed">{t("todo.completed")}</option>
          </select>
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t("todo.allPriorities")}</option>
            <option value="high">{t("todo.high")}</option>
            <option value="medium">{t("todo.medium")}</option>
            <option value="low">{t("todo.low")}</option>
          </select>
        </div>
      )}

      {!compact && showForm && (
        <div className="todo-form-overlay">
          <div className="todo-form">
            <h3>{editingTodo ? t("todo.editTask") : t("todo.newTaskTitle")}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="todo-title">{t("todo.title")} *</label>
                <input
                  id="todo-title"
                  type="text"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="todo-description">
                  {t("todo.description")}
                </label>
                <textarea
                  id="todo-description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  rows={3}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="todo-priority">Priority</label>
                  <select
                    id="todo-priority"
                    value={formData.priority}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        priority: e.target.value as "low" | "medium" | "high",
                      })
                    }
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="todo-dueDate">Due Date</label>
                  <input
                    id="todo-dueDate"
                    type="date"
                    value={formData.dueDate}
                    onChange={(e) =>
                      setFormData({ ...formData, dueDate: e.target.value })
                    }
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="todo-relatedType">Related To</label>
                <select
                  id="todo-relatedType"
                  value={formData.relatedEntityType}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      relatedEntityType: e.target.value,
                      relatedEntityId: "",
                    })
                  }
                >
                  <option value="">None</option>
                  <option value="candidate">Candidate</option>
                  <option value="customer">Customer</option>
                  <option value="job">Job</option>
                  <option value="application">Application</option>
                </select>
              </div>
              {formData.relatedEntityType && (
                <div className="form-group">
                  <label>
                    {formData.relatedEntityType === "candidate" &&
                      "Select Candidate"}
                    {formData.relatedEntityType === "customer" &&
                      "Select Customer"}
                    {formData.relatedEntityType === "job" && "Select Job"}
                    {formData.relatedEntityType === "application" &&
                      "Select Application"}
                  </label>
                  {formData.relatedEntityType === "candidate" && (
                    <select
                      value={formData.relatedEntityId}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          relatedEntityId: e.target.value,
                        })
                      }
                    >
                      <option value="">Select a candidate</option>
                      {candidatesData?.map((candidate) => (
                        <option key={candidate.id} value={candidate.id}>
                          {candidate.first_name} {candidate.last_name}{" "}
                          {candidate.email ? `(${candidate.email})` : ""}
                        </option>
                      ))}
                    </select>
                  )}
                  {formData.relatedEntityType === "customer" && (
                    <select
                      value={formData.relatedEntityId}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          relatedEntityId: e.target.value,
                        })
                      }
                    >
                      <option value="">Select a customer</option>
                      {customersData?.map((customer) => (
                        <option key={customer.id} value={customer.id}>
                          {customer.name}{" "}
                          {customer.company_name
                            ? `(${customer.company_name})`
                            : ""}
                        </option>
                      ))}
                    </select>
                  )}
                  {formData.relatedEntityType === "job" && (
                    <select
                      value={formData.relatedEntityId}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          relatedEntityId: e.target.value,
                        })
                      }
                    >
                      <option value="">Select a job</option>
                      {jobsData?.map((job) => (
                        <option key={job.id} value={job.id}>
                          {job.title}{" "}
                          {job.department ? `- ${job.department}` : ""}{" "}
                          {job.location ? `(${job.location})` : ""}
                        </option>
                      ))}
                    </select>
                  )}
                  {formData.relatedEntityType === "application" && (
                    <select
                      value={formData.relatedEntityId}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          relatedEntityId: e.target.value,
                        })
                      }
                    >
                      <option value="">Select an application</option>
                      {applicationsData?.map((application) => {
                        const candidateName =
                          application.candidate_first_name &&
                          application.candidate_last_name
                            ? `${application.candidate_first_name} ${application.candidate_last_name}`
                            : "Unknown Candidate";
                        return (
                          <option key={application.id} value={application.id}>
                            {candidateName} -{" "}
                            {application.job_title || "Unknown Job"}
                          </option>
                        );
                      })}
                    </select>
                  )}
                </div>
              )}
              <div className="form-actions">
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={
                    createMutation.isLoading || updateMutation.isLoading
                  }
                >
                  {editingTodo ? "Update" : "Create"}
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="todo-sections">
        <div className="todo-section">
          <h3>Pending ({pendingTodos.length})</h3>
          {displayPendingTodos.length === 0 ? (
            <div className="todo-empty">No pending tasks</div>
          ) : (
            <div className="todo-items">
              {displayPendingTodos.map((todo) => (
                <React.Fragment key={todo.id}>
                  {renderTodoItem(todo)}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>

        {showFilters && (
          <div className="todo-section">
            <h3>Completed ({completedTodos.length})</h3>
            {displayCompletedTodos.length === 0 ? (
              <div className="todo-empty">No completed tasks</div>
            ) : (
              <div className="todo-items">
                {displayCompletedTodos.map((todo) => (
                  <React.Fragment key={todo.id}>
                    {renderTodoItem(todo)}
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TodoList;
